import {useMemo} from "react";
import {type Producer} from "@/ReactInit/Scripts/DashboardPage/types";
import {DashboardButton} from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import {formatOrgnr} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardFormatting";
import {normalizeProductKey} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";

interface UserAdministrationProps {
  users: Producer[];
  searchTerm: string;
  sortOption: string;
  onResetFilters: () => void;
  onRefreshUsers: () => Promise<void>;
  onSelectProducer: (producerId: string) => void;
  onCreateProducer: () => void;
  visibleCount: number;
  onChangeVisibleCount: (next: number) => void;
}

type OrgRow = {
  producerId: string;
  virksomhet: string;
  orgnr: string;
  userCount: number;
  orderIndex: number;
};

const AdminDashboardProducerPage = (
  {
    users,
    searchTerm,
    sortOption,
    onResetFilters,
    onSelectProducer,
    onCreateProducer,
    visibleCount,
    onChangeVisibleCount,
  }: UserAdministrationProps) => {

  const orgRows: OrgRow[] = useMemo(() => {
    return users.map((producer, idx) => ({
      producerId: producer.id,
      virksomhet: producer.name ?? "",
      orgnr: producer.orgnr ?? "",
      userCount: (producer.phoneNumbers ?? []).filter((ph) => !ph.deactivated).length,
      orderIndex: idx,
    }));
  }, [users]);

  const normalizedSearch = normalizeProductKey(searchTerm);

  const filteredOrgRows = useMemo(() => {
    if (!normalizedSearch) return orgRows;

    return orgRows.filter((row) =>
      [row.virksomhet, row.orgnr]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(normalizedSearch))
    );
  }, [orgRows, normalizedSearch]);

  const sortedOrgRows = useMemo(() => {
    const copy = [...filteredOrgRows];

    switch (sortOption) {
      case "Eldste":
        return copy.sort((a, b) => a.orderIndex - b.orderIndex);
      case "Alfabetisk":
        return copy.sort((a, b) => a.virksomhet.localeCompare(b.virksomhet, "nb"));
      case "Nyeste":
      default:
        return copy.sort((a, b) => b.orderIndex - a.orderIndex);
    }
  }, [filteredOrgRows, sortOption]);

  const totalProducers = sortedOrgRows.length;
  const effectiveVisibleCount = Math.min(visibleCount, totalProducers);
  const visibleRows = sortedOrgRows.slice(0, effectiveVisibleCount);
  const canShowMore = effectiveVisibleCount < totalProducers;

  if (sortedOrgRows.length === 0) {
    const hasSearch = normalizedSearch.length > 0;
    return (
      <div className="flex flex-col gap-5 tablet:gap-10">
        <DashboardButton
          onClick={onCreateProducer}
          label="Legg til ny virksomhet"
          variant="sidebar"
          rounded="small"
          icon="icon-plus"
          width="fit"
        />
        <p>{hasSearch ? "Ingen virksomheter funnet." : "Ingen virksomheter er lagt til ennå."}</p>
        {hasSearch && (
          <DashboardButton
            onClick={onResetFilters}
            label="Nullstill søk"
            variant="primary"
            width="fit"
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 tablet:gap-10">
      <DashboardButton
        onClick={onCreateProducer}
        label="Legg til ny virksomhet"
        variant="sidebar"
        rounded="small"
        icon="icon-plus"
        width="fit"
      />
      <div>
        <p>Viser {effectiveVisibleCount} av {totalProducers} virksomheter</p>
      </div>

      <div className="min-w-0 overflow-x-auto pb-2.5">
        <table className="dashboardTable w-max min-w-max whitespace-nowrap">
          <thead>
          <tr>
            <th>Virksomhet</th>
            <th>Organisasjonsnummer</th>
            <th>Antall</th>
          </tr>
          </thead>
          <tbody>
          {visibleRows.map((row) => {
            const handleRowActivate = () => onSelectProducer(row.producerId);

            return (
              <tr
                key={row.producerId}
                className="cursor-pointer"
                tabIndex={0}
                role="button"
                aria-label={`Åpne ${row.virksomhet}`}
                onClick={handleRowActivate}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleRowActivate();
                  }
                }}
              >
                <td><span className="truncate">{row.virksomhet}</span></td>
                <td>{formatOrgnr(row.orgnr)}</td>
                <td>{row.userCount}</td>
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>
      {canShowMore && (
        <div className="flex justify-center">
          <DashboardButton
            label="Vis flere"
            variant="darkplain"
            rounded="full"
            onClick={() => onChangeVisibleCount(effectiveVisibleCount + 10)}
          />
        </div>
      )}
    </div>
  )
}

export default AdminDashboardProducerPage;
